<script lang="ts" setup>
import { computed, ref } from 'vue'
import { formatFileSize } from '../../../utils/fileSize'
import type { IContent, IFaqItem } from '../types'
import useBreakpoint from '@/ui/composables/useBreakpoint'
import { SIZES } from '@/ui/ts/types/Sizes'

const props = defineProps<{
    content: IContent
    darkmode: boolean
}>()

const { isMobile } = useBreakpoint()
const showDocs = ref(false)
const showFaq = ref(false)

function getItems<T>(items: T[], readmore: boolean) {
    if (!items) return []
    return readmore ? items.slice(0, 5) : items
}

const faqItems = ref(getItems(props.content?.faq?.items, props.content?.faq?.readmore))
const faqItemsExpanded = ref(props.content.faq?.items?.slice(5))
const docsItems = ref(getItems(props.content?.docs?.items, props.content?.docs?.readmore))
const docsItemsExpanded = ref(props.content.docs?.items?.slice(5))

const faqSize = computed(() => (isMobile.value ? SIZES.SMALL : SIZES.MEDIUM))
const docsSize = computed(() => (isMobile.value ? SIZES.MEDIUM : SIZES.LARGE))

function toggleAccordionItem(accordionItem: IFaqItem, isOpen: boolean) {
    accordionItem.isOpen = isOpen
}

function toggleShowDocs() {
    showDocs.value = !showDocs.value
}

function toggleShowFaq() {
    showFaq.value = !showFaq.value
}
</script>

<template>
    <template v-if="content.faq">
        <div class="transition-all duration-500 ease-in-out">
            <VAccordionItem
                v-for="(accordionItem, index) in faqItems"
                :key="`accordion_item_faq_${index}`"
                v-model:is-open="accordionItem.isOpen"
                :data-test="`accordion_item_faq_${index}`"
                :item="{
                    value: accordionItem.title,
                    title: accordionItem.title,
                    isOpen: accordionItem.isOpen,
                }"
                :size="faqSize"
                :dark="darkmode"
                :hide-divider="!showFaq && index === faqItems.length - 1"
                @update:is-open="toggleAccordionItem(accordionItem, $event)"
            >
                <div v-html-gpp="accordionItem.description.value" />
            </VAccordionItem>

            <VTransition v-model="showFaq">
                <VAccordionItem
                    v-for="(accordionItem, index) in faqItemsExpanded"
                    :key="`accordion_item_faq_${index}`"
                    v-model:is-open="accordionItem.isOpen"
                    :data-test="`accordion_item_faq_${index}`"
                    :item="{
                        value: accordionItem.title,
                        title: accordionItem.title,
                        isOpen: accordionItem.isOpen,
                    }"
                    :size="faqSize"
                    :dark="darkmode"
                    :hide-divider="index === faqItems.length - 1"
                    @update:is-open="toggleAccordionItem(accordionItem, $event)"
                >
                    <div v-html-gpp="accordionItem.description.value" />
                </VAccordionItem>
            </VTransition>

            <VLink
                v-if="content.faq.readmore && content.faq.items.length > 5"
                class="mt-4"
                size="MEDIUM"
                data-test="faq_toggle_button"
                :trailing-icon="showFaq ? 'chevron-up' : 'chevron-down'"
                :dark="darkmode"
                @click="toggleShowFaq"
            >
                {{ showFaq ? 'Zobacz mniej' : 'Zobacz więcej' }}
            </VLink>
        </div>
    </template>

    <template v-if="content.docs">
        <div class="transition-all duration-500 ease-in-out">
            <template
                v-for="(docsItem, index) in docsItems"
                :key="`linklist_item_docs_${index}`"
            >
                <VLinkList
                    v-if="docsItem?.file"
                    :size="docsSize"
                    data-test="data-test"
                    :dark="darkmode"
                    :link-label="docsItem?.name"
                    target="_blank"
                    :href="docsItem?.file?.links?.site?.href"
                    leading-icon="document"
                >
                    <template #additionalText>
                        {{ formatFileSize(docsItem?.file?.size, docsItem?.file?.filename) }}
                    </template>
                </VLinkList>
            </template>
        </div>

        <VTransition v-model="showDocs">
            <template
                v-for="(docsItem, index) in docsItemsExpanded"
                :key="`linklist_item_docs_${index}`"
            >
                <VLinkList
                    v-if="docsItem?.file"
                    :size="docsSize"
                    data-test="data-test"
                    :dark="darkmode"
                    :link-label="docsItem?.name"
                    target="_blank"
                    :href="docsItem?.file?.links?.site?.href"
                    leading-icon="document"
                >
                    <template #additionalText>
                        {{ formatFileSize(docsItem?.file?.size, docsItem?.file?.filename) }}
                    </template>
                </VLinkList>
            </template>
        </VTransition>

        <VLink
            v-if="content.docs.readmore && content.docs.items.length > 5"
            class="mt-4"
            size="MEDIUM"
            data-test="docs_toggle_button"
            :trailing-icon="showDocs ? 'chevron-up' : 'chevron-down'"
            :dark="darkmode"
            @click="toggleShowDocs"
        >
            {{ showDocs ? 'Zobacz mniej' : 'Zobacz więcej' }}
        </VLink>
    </template>
</template>
